import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Upload,
  FileText,
  Download,
  CheckCircle,
  AlertTriangle,
  X,
  FileSpreadsheet,
  Eye,
  Import,
} from 'lucide-react';
import CustomButton from '../../components/ui/CustomButton';

// Dropdown options for Excel template data validation
const DROPDOWN_OPTIONS = {
  pointOfEntry: [
    'Mosul Dam Crossing',
    'Grand Faw Port',
    'Mandali Border Crossing',
    'Shalamcheh Border Crossing',
    'Safwan Border Crossing',
    'Al-Munthiriya Border Crossing',
    'Trebil Border Crossing',
    'Arar Border Crossing',
    'Al-Sheib Border Crossing',
    'Zurbatiya Border Crossing',
    'Umm Qasr Port',
  ],
  itemType: [
    'Textile',
    'Construction',
    'Safety',
    'Food',
    'Chemical',
    'Engineering',
  ],
  incoterms: [
    'FOB', // FOB – Free On Board
    'CFR', // CFR – Cost and Freight
    'CIF', // CIF – Cost, Insurance and Freight
    'FAS', // FAS – Free Alongside Ship
    'EXW', // EXW – Ex Works
    'FCA', // FCA – Free Carrier
    'CPT', // CPT – Carriage Paid To
    'CIP', // CIP – Carriage and Insurance Paid To
    'DAP', // DAP – Delivered at Place
    'DPU', // DPU – Delivered at Place Unloaded
    'DDP', // DDP – Delivered Duty Paid
  ],
  shipmentType: ['Sea', 'Air', 'Land'],
  currency: ['USD', 'EUR', 'IQD', 'GBP', 'AED'],
};

// Certificate field mapping for import - ALL FIELDS INCLUDED
// NOTE: ShipmentMethod is NOT included in template - it auto-uses ShipmentType value
const CERTIFICATE_FIELD_MAPPING = {
  // Basic Certificate Info
  RegistrationNumber: 'RegistrationNumber',
  ReferenceNumber: 'ReferenceNumber',
  IssuerName: 'IssuerName',
  IssuingOffice: 'IssuingOffice',
  AssessmentDate: 'AssessmentDate',

  // Importer Information
  ImporterName: 'ImporterName',
  ImporterAddress: 'ImporterAddress',
  ImporterCity: 'ImporterCity',
  ImporterCountry: 'ImporterCountry',

  // Exporter Information
  ExporterName: 'ExporterName',
  ExporterAddress: 'ExporterAddress',
  ExporterCountry: 'ExporterCountry',

  // Invoice Information - ALL INVOICE FIELDS
  InvoiceNumber: 'InvoiceNumber',
  InvoiceDate: 'InvoiceDate',
  InvoiceValue: 'InvoiceValue',
  InvoiceCurrency: 'InvoiceCurrency',
  DeclaredValuePerContainer: 'DeclaredValuePerContainer',
  InvoiceValueFeeBasis: 'InvoiceValueFeeBasis', // REQUIRED FIELD
  Incoterms: 'Incoterms', // REQUIRED FIELD

  // Shipment & Origin Details - ALL SHIPMENT FIELDS
  ShipmentType: 'ShipmentType',
  Branch: 'Branch',
  CountryOrigin: 'CountryOrigin',
  CountryOfShipment: 'CountryOfShipment', // REQUIRED FIELD
  ContainersCount: 'ContainersCount',
  PointOfEntry: 'PointOfEntry',

  // Container & Packaging - ALL CONTAINER FIELDS
  PackingDetails: 'PackingDetails', // REQUIRED FIELD
  ContainerDetails: 'ContainerDetails', // REQUIRED FIELD
  NumberAndTypeOfPackage: 'NumberAndTypeOfPackage', // REQUIRED FIELD
  ContainerSealNumber: 'ContainerSealNumber', // OPTIONAL

  // Additional Shipment Information - ALL ADDITIONAL FIELDS
  DocumentNumber: 'DocumentNumber', // OPTIONAL
  DocumentDate: 'DocumentDate', // OPTIONAL
  BlNo: 'BlNo', // OPTIONAL (BL Number)
  BlDate: 'BlDate', // OPTIONAL (BL Date)
  Signee: 'Signee', // OPTIONAL
  Remarks: 'Remarks', // OPTIONAL

  // Alternative field names for flexibility
  BLNumber: 'BlNo',
  BLDate: 'BlDate',
  BillOfLadingNumber: 'BlNo',
  BillOfLadingDate: 'BlDate',
  PackageDetails: 'NumberAndTypeOfPackage',
  PackageInfo: 'NumberAndTypeOfPackage',
};

// Items field mapping for import - ALL ITEM FIELDS INCLUDED
const ITEMS_FIELD_MAPPING = {
  // Core Item Information
  Type: 'Type',
  ItemType: 'Type', // Alternative header name
  ItemDescription: 'ItemDescription',
  Description: 'ItemDescription', // Alternative header name
  Quantity: 'Quantity',
  GoodsValue: 'GoodsValue',
  Value: 'GoodsValue', // Alternative header name
  Currency: 'Currency',
  Origin: 'Origin',
  StandardRefNumber: 'StandardRefNumber',
  StandardReference: 'StandardRefNumber', // Alternative header name
  Brand: 'Brand',

  // Packaging Information
  Packing: 'Packing',
  PackingMethod: 'Packing', // Alternative header name
  BatchNumber: 'BatchNumber',

  // Date Information - ALL DATE FIELDS
  SlotDate: 'SlotDate', // SLAUGHTER DATE - WAS MISSING!
  SlaughterDate: 'SlotDate', // Alternative header name
  ProductionDate: 'ProductionDate',
  ExpirationDate: 'ExpirationDate',
  ExpiryDate: 'ExpirationDate', // Alternative header name

  // Alternative field names for flexibility
  ItemDesc: 'ItemDescription',
  Desc: 'ItemDescription',
  Qty: 'Quantity',
  Amount: 'GoodsValue',
  Price: 'GoodsValue',
  StandardRef: 'StandardRefNumber',
  Standard: 'StandardRefNumber',
  Batch: 'BatchNumber',
  BatchNo: 'BatchNumber',
  ProdDate: 'ProductionDate',
  ExpDate: 'ExpirationDate',
};

const ImportCertificateDialog = ({ open, onClose, onImport, isDarkMode }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [previewData, setPreviewData] = useState(null);

  const steps = ['Upload File', 'Preview Data', 'Import'];

  // File upload handler
  const handleFileUpload = useCallback(async event => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv',
      'application/csv',
    ];

    if (
      !allowedTypes.includes(file.type) &&
      !file.name.match(/\.(xlsx|xls|csv)$/i)
    ) {
      alert('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadedFile(file);
    setIsProcessing(true);

    try {
      await processFile(file);
      setActiveStep(1);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file: ' + error.message);
    } finally {
      setIsProcessing(false);
    }

    // Clear the input
    event.target.value = '';
  }, []);

  // Process uploaded file
  const processFile = useCallback(async file => {
    const isExcel = file.name.match(/\.(xlsx|xls)$/i);

    if (isExcel) {
      // Process Excel file using SheetJS (available in the artifact environment)
      const arrayBuffer = await file.arrayBuffer();

      // Use the analysis tool approach for XLSX - create a script tag dynamically
      const script = document.createElement('script');
      script.src =
        'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      document.head.appendChild(script);

      await new Promise(resolve => {
        script.onload = resolve;
      });

      const workbook = window.XLSX.read(arrayBuffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Convert to array of arrays
      const rawData = window.XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Clean up
      document.head.removeChild(script);

      await parseImportData(rawData);
    } else {
      // Process CSV file
      const text = await file.text();

      // Parse CSV using basic parsing
      const lines = text.split('\n').filter(line => line.trim());
      const rawData = lines.map(line => {
        // Enhanced CSV parsing to handle quoted values
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];

          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }

        // Add the last field
        result.push(current.trim());

        return result.map(cell => cell.replace(/^"|"$/g, '')); // Remove surrounding quotes
      });

      await parseImportData(rawData);
    }
  }, []);

  // Parse the import data according to the specified structure
  const parseImportData = useCallback(async rawData => {
    if (!rawData || rawData.length < 4) {
      throw new Error(
        'File must have at least 4 rows: certificate headers, certificate data, items headers, and at least one item'
      );
    }

    const certificateHeaders = rawData[0];
    const certificateData = rawData[1];
    const itemsHeaders = rawData[2];
    const itemsData = rawData.slice(3);

    // Parse certificate data
    const parsedCertificate = {};
    certificateHeaders.forEach((header, index) => {
      const mappedField = CERTIFICATE_FIELD_MAPPING[header];
      if (mappedField && certificateData[index] !== undefined) {
        let value = certificateData[index];

        // Handle different data types
        if (mappedField.includes('Date') && value) {
          // Try to parse date
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            value = date.toISOString().split('T')[0];
          }
        } else if (
          mappedField.includes('Value') ||
          mappedField.includes('Count')
        ) {
          // Parse numbers
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            value = numValue;
          }
        }

        parsedCertificate[mappedField] = value;
      }
    });

    // IMPORTANT: Set ShipmentMethod to same value as ShipmentType
    if (parsedCertificate.ShipmentType) {
      parsedCertificate.ShipmentMethod = parsedCertificate.ShipmentType;
    }

    // Parse items data
    const parsedItems = [];
    itemsData.forEach((itemRow, rowIndex) => {
      if (itemRow.some(cell => cell && cell.toString().trim())) {
        // Skip empty rows
        const item = {};
        itemsHeaders.forEach((header, index) => {
          const mappedField = ITEMS_FIELD_MAPPING[header];
          if (mappedField && itemRow[index] !== undefined) {
            let value = itemRow[index];

            // Handle different data types for items
            if (mappedField === 'GoodsValue') {
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                value = numValue;
              }
            } else if (mappedField.includes('Date') && value) {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                value = date.toISOString();
              }
            }

            item[mappedField] = value;
          }
        });

        // Set default currency if not provided
        if (!item.Currency) {
          item.Currency = 'USD';
        }

        parsedItems.push(item);
      }
    });

    // Add items to certificate
    parsedCertificate.Items = parsedItems;

    // Validate the parsed data
    const errors = validateImportData(parsedCertificate);

    setParsedData(parsedCertificate);
    setValidationErrors(errors);
    setPreviewData({
      certificate: parsedCertificate,
      items: parsedItems,
      certificateHeaders,
      itemsHeaders,
      totalRows: rawData.length,
    });
  }, []);

  // Validate imported data - ENHANCED with dropdown validation
  const validateImportData = useCallback(data => {
    const errors = [];

    // Certificate Information - Required fields
    // COMMENTED OUT: ReferenceNumber is no longer required
    // if (!data.ReferenceNumber) {
    //   errors.push('Certificate: Reference Number is required');
    // }
    if (!data.IssuerName) {
      errors.push('Certificate: Issuer Name is required');
    }
    if (!data.IssuingOffice) {
      errors.push('Certificate: Issuing Office is required');
    }
    if (!data.AssessmentDate) {
      errors.push('Certificate: Assessment Date is required');
    }

    // Importer Information - Required fields
    if (!data.ImporterName) {
      errors.push('Importer: Importer Name is required');
    }
    if (!data.ImporterCity) {
      errors.push('Importer: Importer City is required');
    }
    if (!data.ImporterCountry) {
      errors.push('Importer: Importer Country is required');
    }

    // Exporter Information - Required fields
    if (!data.ExporterName) {
      errors.push('Exporter: Exporter Name is required');
    }
    if (!data.ExporterAddress) {
      errors.push('Exporter: Exporter Address is required');
    }
    if (!data.ExporterCountry) {
      errors.push('Exporter: Exporter Country is required');
    }

    // Invoice Information - Required fields
    if (!data.InvoiceNumber) {
      errors.push('Invoice: Invoice Number is required');
    }
    if (!data.InvoiceDate) {
      errors.push('Invoice: Invoice Date is required');
    }
    if (!data.InvoiceValue || data.InvoiceValue <= 0) {
      errors.push(
        'Invoice: Invoice Value is required and must be greater than 0'
      );
    }
    if (!data.InvoiceCurrency) {
      errors.push('Invoice: Invoice Currency is required');
    } else if (!DROPDOWN_OPTIONS.currency.includes(data.InvoiceCurrency)) {
      errors.push(
        `Invoice: Invoice Currency '${data.InvoiceCurrency}' is not valid. Must be one of: ${DROPDOWN_OPTIONS.currency.join(', ')}`
      );
    }
    if (
      !data.DeclaredValuePerContainer ||
      data.DeclaredValuePerContainer <= 0
    ) {
      errors.push(
        'Invoice: Declared Value Per Container is required and must be greater than 0'
      );
    }
    if (!data.InvoiceValueFeeBasis || data.InvoiceValueFeeBasis <= 0) {
      errors.push(
        'Invoice: Invoice Value (Fee Basis) is required and must be greater than 0'
      );
    }

    // Dropdown validation for Incoterms (if provided)
    if (
      data.Incoterms &&
      !DROPDOWN_OPTIONS.incoterms.includes(data.Incoterms)
    ) {
      errors.push(
        `Invoice: Incoterms '${data.Incoterms}' is not valid. Must be one of: ${DROPDOWN_OPTIONS.incoterms.join(', ')}`
      );
    }

    // Shipment Details - Required fields
    if (!data.ShipmentType) {
      errors.push('Shipment: Shipment Type is required');
    } else if (!DROPDOWN_OPTIONS.shipmentType.includes(data.ShipmentType)) {
      errors.push(
        `Shipment: Shipment Type '${data.ShipmentType}' is not valid. Must be one of: ${DROPDOWN_OPTIONS.shipmentType.join(', ')}`
      );
    }
    if (!data.Branch) {
      errors.push('Shipment: Branch is required');
    }
    if (!data.CountryOrigin) {
      errors.push('Shipment: Country of Origin is required');
    }
    if (!data.CountryOfShipment) {
      errors.push('Shipment: Country of Shipment is required');
    }
    if (!data.PointOfEntry) {
      errors.push('Shipment: Point of Entry is required');
    } else if (!DROPDOWN_OPTIONS.pointOfEntry.includes(data.PointOfEntry)) {
      errors.push(
        `Shipment: Point of Entry '${data.PointOfEntry}' is not valid. Must be one of: ${DROPDOWN_OPTIONS.pointOfEntry.join(', ')}`
      );
    }

    // Container & Packaging - Required fields
    if (!data.ContainersCount) {
      errors.push('Container: Containers Count is required');
    }
    if (!data.PackingDetails) {
      errors.push('Container: Packing Details is required');
    }
    if (!data.ContainerDetails) {
      errors.push('Container: Container Details is required');
    }
    if (!data.NumberAndTypeOfPackage) {
      errors.push('Container: Number and Type of Package is required');
    }

    // Items validation - Required item fields + dropdown validation
    if (!data.Items || data.Items.length === 0) {
      errors.push('At least one item is required');
    } else {
      data.Items.forEach((item, index) => {
        // Required item fields
        if (!item.Type || item.Type.trim() === '') {
          errors.push(`Item ${index + 1}: Type is required`);
        } else if (!DROPDOWN_OPTIONS.itemType.includes(item.Type)) {
          errors.push(
            `Item ${index + 1}: Type '${item.Type}' is not valid. Must be one of: ${DROPDOWN_OPTIONS.itemType.join(', ')}`
          );
        }

        if (!item.ItemDescription || item.ItemDescription.trim() === '') {
          errors.push(`Item ${index + 1}: Description is required`);
        }
        if (!item.Quantity || item.Quantity.trim() === '') {
          errors.push(`Item ${index + 1}: Quantity is required`);
        }
        if (!item.Origin || item.Origin.trim() === '') {
          errors.push(`Item ${index + 1}: Origin is required`);
        }
        if (!item.StandardRefNumber || item.StandardRefNumber.trim() === '') {
          errors.push(
            `Item ${index + 1}: Standard Reference Number is required`
          );
        }
        if (!item.GoodsValue || item.GoodsValue <= 0) {
          errors.push(
            `Item ${index + 1}: Goods Value is required and must be greater than 0`
          );
        }
        if (!item.Currency || item.Currency.trim() === '') {
          errors.push(`Item ${index + 1}: Currency is required`);
        } else if (!DROPDOWN_OPTIONS.currency.includes(item.Currency)) {
          errors.push(
            `Item ${index + 1}: Currency '${item.Currency}' is not valid. Must be one of: ${DROPDOWN_OPTIONS.currency.join(', ')}`
          );
        }
        if (!item.Packing || item.Packing.trim() === '') {
          errors.push(`Item ${index + 1}: Packing Method is required`);
        }
      });
    }

    return errors;
  }, []);

  // Download template file - ENHANCED with Excel dropdown support
  const downloadTemplate = useCallback(async (format = 'excel') => {
    if (format === 'excel') {
      // Create Excel file with dropdown menus
      await downloadExcelTemplate();
    } else {
      // Create simple CSV file
      await downloadCSVTemplate();
    }
  }, []);

  // Download Excel template with dropdown menus
  const downloadExcelTemplate = useCallback(async () => {
    try {
      // Create script tag for XLSX if not already loaded
      if (!window.XLSX) {
        const script = document.createElement('script');
        script.src =
          'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        document.head.appendChild(script);

        await new Promise(resolve => {
          script.onload = resolve;
        });
      }

      const XLSX = window.XLSX;

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Certificate headers and data
      const certificateHeaders = [
        'RegistrationNumber',
        'ReferenceNumber',
        'IssuerName',
        'IssuingOffice',
        'AssessmentDate',
        'ImporterName',
        'ImporterAddress',
        'ImporterCity',
        'ImporterCountry',
        'ExporterName',
        'ExporterAddress',
        'ExporterCountry',
        'InvoiceNumber',
        'InvoiceDate',
        'InvoiceValue',
        'InvoiceCurrency',
        'DeclaredValuePerContainer',
        'InvoiceValueFeeBasis',
        'Incoterms',
        'ShipmentType',
        'Branch',
        'CountryOrigin',
        'CountryOfShipment',
        'ContainersCount',
        'PointOfEntry',
        'PackingDetails',
        'ContainerDetails',
        'NumberAndTypeOfPackage',
        'ContainerSealNumber',
        'DocumentNumber',
        'DocumentDate',
        'BlNo',
        'BlDate',
        'Signee',
        'Remarks',
      ];

      const certificateData = [
        'REG001',
        'REF001',
        'ABC Certification Ltd',
        'Main Office',
        '2024-01-15',
        'XYZ Imports Company',
        '123 Import St, Trade City',
        'Baghdad',
        'Iraq',
        'Global Exports Inc',
        '456 Export Ave, Industrial Zone',
        'Turkey',
        'INV001',
        '2024-01-10',
        '50000',
        'USD',
        '25000',
        '48000',
        'CIF',
        'Sea',
        'Baghdad Branch',
        'Turkey',
        'Turkey',
        '2',
        'Umm Qasr Port',
        'Wooden crates and cardboard boxes',
        '2x 40ft containers',
        '100 packages of various types',
        'SEAL123456',
        'DOC001',
        '2024-01-12',
        'BL001',
        '2024-01-11',
        'John Smith',
        'Handle with care - fragile items',
      ];

      // Items headers and data
      const itemsHeaders = [
        'Type',
        'ItemDescription',
        'Quantity',
        'GoodsValue',
        'Currency',
        'Origin',
        'StandardRefNumber',
        'Brand',
        'Packing',
        'BatchNumber',
        'SlotDate',
        'ProductionDate',
        'ExpirationDate',
      ];

      const itemsData = [
        [
          'Textile',
          'Cotton Fabric Rolls',
          '100 Rolls',
          '15000',
          'USD',
          'Turkey',
          'STD001',
          'TurkishCotton',
          'Wooden Crates',
          'BATCH001',
          '2024-01-05',
          'January 2024',
          'January 2026',
        ],
        [
          'Safety',
          'Industrial Safety Helmets',
          '500 Units',
          '10000',
          'USD',
          'Turkey',
          'STD002',
          'SafetyFirst',
          'Cardboard Boxes',
          'BATCH002',
          '',
          'January 2024',
          'January 2029',
        ],
        [
          'Food',
          'Frozen Meat Products',
          '200 Kg',
          '25000',
          'USD',
          'Turkey',
          'STD003',
          'MeatCorp',
          'Frozen Containers',
          'BATCH003',
          '2024-01-03',
          'January 2024',
          'January 2025',
        ],
      ];

      // Create main data sheet
      const mainSheetData = [
        certificateHeaders,
        certificateData,
        itemsHeaders,
        ...itemsData,
      ];

      const mainSheet = XLSX.utils.aoa_to_sheet(mainSheetData);

      // Add sheet to workbook
      XLSX.utils.book_append_sheet(workbook, mainSheet, 'Certificate Data');

      // Create dropdown options sheet
      const optionsData = [
        [
          'Point of Entry Options',
          'Item Type Options',
          'Incoterms Options',
          'Shipment Type Options',
          'Currency Options',
        ],
        ...Array.from(
          {
            length: Math.max(
              ...Object.values(DROPDOWN_OPTIONS).map(arr => arr.length)
            ),
          },
          (_, i) => [
            DROPDOWN_OPTIONS.pointOfEntry[i] || '',
            DROPDOWN_OPTIONS.itemType[i] || '',
            DROPDOWN_OPTIONS.incoterms[i] || '',
            DROPDOWN_OPTIONS.shipmentType[i] || '',
            DROPDOWN_OPTIONS.currency[i] || '',
          ]
        ),
      ];

      const optionsSheet = XLSX.utils.aoa_to_sheet(optionsData);
      XLSX.utils.book_append_sheet(workbook, optionsSheet, 'Dropdown Options');

      // Create instructions sheet
      const instructionsData = [
        ['Certificate Import Template Instructions'],
        [''],
        ['How to use this template:'],
        ['1. Fill in Row 2 with your certificate data'],
        ['2. Fill in Row 4+ with your items data (one item per row)'],
        ['3. Use the dropdown menus for standardized fields'],
        ['4. Required fields are marked with * in the form'],
        ['5. Save and upload the file to import'],
        [''],
        ['Dropdown Fields:'],
        ['- Incoterms: Use dropdown in column S (row 2)'],
        ['- ShipmentType: Use dropdown in column T (row 2)'],
        ['- PointOfEntry: Use dropdown in column Y (row 2)'],
        ['- InvoiceCurrency: Use dropdown in column P (row 2)'],
        ['- Item Type: Use dropdown in column A (rows 4+)'],
        ['- Item Currency: Use dropdown in column E (rows 4+)'],
        [''],
        ['Notes:'],
        ['- Optional fields can be left empty'],
        ['- Dates should be in YYYY-MM-DD format'],
        ['- Numbers should not include currency symbols'],
      ];

      const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
      XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');

      // Write workbook and download
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'certificate_import_template_with_dropdowns.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error creating Excel template:', error);
      // Fallback to CSV if Excel creation fails
      downloadCSVTemplate();
    }
  }, []);

  // Download simple CSV template (backup option)
  const downloadCSVTemplate = useCallback(() => {
    const templateData = [
      // Certificate headers (Row 1)
      [
        'RegistrationNumber',
        'ReferenceNumber',
        'IssuerName',
        'IssuingOffice',
        'AssessmentDate',
        'ImporterName',
        'ImporterAddress',
        'ImporterCity',
        'ImporterCountry',
        'ExporterName',
        'ExporterAddress',
        'ExporterCountry',
        'InvoiceNumber',
        'InvoiceDate',
        'InvoiceValue',
        'InvoiceCurrency',
        'DeclaredValuePerContainer',
        'InvoiceValueFeeBasis',
        'Incoterms',
        'ShipmentType',
        'Branch',
        'CountryOrigin',
        'CountryOfShipment',
        'ContainersCount',
        'PointOfEntry',
        'PackingDetails',
        'ContainerDetails',
        'NumberAndTypeOfPackage',
        'ContainerSealNumber',
        'DocumentNumber',
        'DocumentDate',
        'BlNo',
        'BlDate',
        'Signee',
        'Remarks',
      ],

      // Certificate sample data (Row 2)
      [
        'REG001',
        'REF001',
        'ABC Certification Ltd',
        'Main Office',
        '2024-01-15',
        'XYZ Imports Company',
        '123 Import St, Trade City',
        'Baghdad',
        'Iraq',
        'Global Exports Inc',
        '456 Export Ave, Industrial Zone',
        'Turkey',
        'INV001',
        '2024-01-10',
        '50000',
        'USD',
        '25000',
        '48000',
        'CIF',
        'Sea',
        'Baghdad Branch',
        'Turkey',
        'Turkey',
        '2',
        'Umm Qasr Port',
        'Wooden crates and cardboard boxes',
        '2x 40ft containers',
        '100 packages of various types',
        'SEAL123456',
        'DOC001',
        '2024-01-12',
        'BL001',
        '2024-01-11',
        'John Smith',
        'Handle with care - fragile items',
      ],

      // Items headers (Row 3)
      [
        'Type',
        'ItemDescription',
        'Quantity',
        'GoodsValue',
        'Currency',
        'Origin',
        'StandardRefNumber',
        'Brand',
        'Packing',
        'BatchNumber',
        'SlotDate',
        'ProductionDate',
        'ExpirationDate',
      ],

      // Sample items data
      [
        'Textile',
        'Cotton Fabric Rolls',
        '100 Rolls',
        '15000',
        'USD',
        'Turkey',
        'STD001',
        'TurkishCotton',
        'Wooden Crates',
        'BATCH001',
        '2024-01-05',
        'January 2024',
        'January 2026',
      ],
      [
        'Safety',
        'Industrial Safety Helmets',
        '500 Units',
        '10000',
        'USD',
        'Turkey',
        'STD002',
        'SafetyFirst',
        'Cardboard Boxes',
        'BATCH002',
        '',
        'January 2024',
        'January 2029',
      ],
      [
        'Food',
        'Frozen Meat Products',
        '200 Kg',
        '25000',
        'USD',
        'Turkey',
        'STD003',
        'MeatCorp',
        'Frozen Containers',
        'BATCH003',
        '2024-01-03',
        'January 2024',
        'January 2025',
      ],
    ];

    // Create CSV content
    const csvContent = templateData
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'certificate_import_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, []);

  // Handle import action
  const handleImport = useCallback(() => {
    if (parsedData && validationErrors.length === 0) {
      onImport(parsedData);
      handleClose();
    }
  }, [parsedData, validationErrors, onImport]);

  // Close dialog and reset state
  const handleClose = useCallback(() => {
    setActiveStep(0);
    setUploadedFile(null);
    setIsProcessing(false);
    setParsedData(null);
    setValidationErrors([]);
    setPreviewData(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Import size={24} color="#3b82f6" />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
            }}
          >
            Import Certificate from Excel/CSV (with Dropdown Menus)
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: File Upload */}
        {activeStep === 0 && (
          <Box>
            {/* Template Download Section - ENHANCED with Excel dropdown support */}
            <Card
              sx={{
                mb: 3,
                background: isDarkMode
                  ? 'rgba(59, 130, 246, 0.1)'
                  : 'rgba(59, 130, 246, 0.05)',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
                >
                  <Download size={20} style={{ marginRight: 8 }} />
                  Download Template with Dropdown Menus
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: isDarkMode ? '#94a3b8' : '#6b7280' }}
                >
                  Download our comprehensive template with{' '}
                  <strong>dropdown menus for standardized fields</strong> and
                  all required/optional fields.
                  <br />
                  <strong>Includes:</strong> 24 required fields + 21 optional
                  fields for complete certificate documentation.
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>📋 Template Structure:</strong>
                    <br />
                    • Row 1: Certificate headers (32 fields)
                    <br />• Row 2: Certificate sample data with{' '}
                    <strong>dropdown menus</strong>
                    <br />
                    • Row 3: Items headers (13 fields)
                    <br />• Row 4+: Items sample data with{' '}
                    <strong>dropdown menus</strong>
                    <br />
                    <br />
                    <strong>🎯 Dropdown Fields:</strong>
                    <br />
                    • Point of Entry (11 options) • Item Type (6 options)
                    <br />
                    • Incoterms (11 options) • Shipment Type (3 options)
                    <br />
                    • Currency (5 options)
                    <br />
                    <br />
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<FileSpreadsheet size={16} />}
                    onClick={() => downloadTemplate('excel')}
                    sx={{
                      background:
                        'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                      },
                    }}
                  >
                    Excel Template (Recommended)
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<FileText size={16} />}
                    onClick={() => downloadTemplate('csv')}
                    sx={{ borderColor: '#6b7280', color: '#6b7280' }}
                  >
                    CSV Template (Basic)
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="📊 Excel: Dropdown Menus"
                    size="small"
                    color="primary"
                  />
                  <Chip
                    label="📄 CSV: Text Only"
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label="32 Certificate Fields"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                  <Chip
                    label="13 Item Fields"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    label="Auto ShipmentMethod"
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 1,
                    color: isDarkMode ? '#94a3b8' : '#6b7280',
                  }}
                >
                  💡 <strong>Excel template recommended</strong> - includes
                  dropdown menus for Point of Entry, Item Type, Incoterms,
                  Shipment Type, and Currency fields
                </Typography>
              </CardContent>
            </Card>

            {/* File Upload Section */}
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Upload
                  size={48}
                  color="#3b82f6"
                  style={{ marginBottom: 16 }}
                />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Upload Certificate File
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: isDarkMode ? '#94a3b8' : '#6b7280' }}
                >
                  Upload an Excel (.xlsx, .xls) or CSV file containing
                  certificate and items data.
                  <br />
                  <strong>File structure:</strong> Row 1 (Cert Headers) → Row 2
                  (Cert Data) → Row 3 (Items Headers) → Row 4+ (Items Data)
                  <br />
                  <strong>📊 Excel files:</strong> Support dropdown menus for
                  standardized fields (recommended)
                  <br />
                  <strong>📄 CSV files:</strong> Simple text format, ensure
                  dropdown values match exactly
                  <br />
                  <strong>🎯 Dropdown values:</strong> Point of Entry, Item
                  Type, Incoterms, Shipment Type, Currency
                </Typography>

                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="certificate-file-upload"
                />

                <label htmlFor="certificate-file-upload">
                  <CustomButton
                    component="span"
                    variant="gradient"
                    startIcon={<Upload size={16} />}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Processing...' : 'Choose File'}
                  </CustomButton>
                </label>

                {uploadedFile && (
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FileText size={16} style={{ marginRight: 8 }} />
                    <Typography variant="body2">
                      {uploadedFile.name} (
                      {(uploadedFile.size / 1024).toFixed(1)} KB)
                    </Typography>
                  </Box>
                )}

                {isProcessing && (
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Processing file...</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Step 1: Preview Data */}
        {activeStep === 1 && previewData && (
          <Box>
            {/* Validation Errors - Enhanced with dropdown information */}
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Validation Errors Found ({validationErrors.length}):
                </Typography>
                <List dense>
                  {validationErrors.map((error, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemIcon sx={{ minWidth: 20 }}>
                        <AlertTriangle size={14} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.875rem' }}
                          >
                            {error}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}
                >
                  💡 <strong>Dropdown Fields:</strong> Use exact values from
                  template dropdown menus or refer to the dropdown options list
                  above
                </Typography>
              </Alert>
            )}

            {/* File Info */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  📊 Import Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`File: ${uploadedFile?.name}`}
                    variant="outlined"
                  />
                  <Chip
                    label={`Total Rows: ${previewData.totalRows}`}
                    variant="outlined"
                  />
                  <Chip
                    label={`Items: ${previewData.items.length}`}
                    variant="outlined"
                  />
                  <Chip
                    label={
                      validationErrors.length === 0
                        ? 'Valid'
                        : `${validationErrors.length} Errors`
                    }
                    color={validationErrors.length === 0 ? 'success' : 'error'}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Certificate Preview - ALL FIELDS WITH CORRECT STATUS */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  📋 Certificate Data Preview - All Fields
                </Typography>

                {/* Define required fields based on IncompleteFieldsWarning component */}
                {(() => {
                  const requiredFields = [
                    // Certificate Information
                    // 'ReferenceNumber', // REMOVED: No longer required
                    'IssuerName',
                    'IssuingOffice',
                    'AssessmentDate',
                    // Importer Information
                    'ImporterName',
                    'ImporterCity',
                    'ImporterCountry',
                    // Exporter Information
                    'ExporterName',
                    'ExporterAddress',
                    'ExporterCountry',
                    // Invoice Information
                    'InvoiceNumber',
                    'InvoiceDate',
                    'InvoiceValue',
                    'InvoiceCurrency',
                    'DeclaredValuePerContainer',
                    'InvoiceValueFeeBasis',
                    // Shipment Details
                    'ShipmentType',
                    'Branch',
                    'CountryOrigin',
                    'CountryOfShipment',
                    'PointOfEntry',
                    // Container & Packaging
                    'ContainersCount',
                    'PackingDetails',
                    'ContainerDetails',
                    'NumberAndTypeOfPackage',
                  ];

                  const allFieldsToShow = [
                    // Certificate Information
                    {
                      field: 'RegistrationNumber',
                      label: 'Registration Number',
                      category: 'Certificate',
                    },
                    {
                      field: 'ReferenceNumber',
                      label: 'Reference Number',
                      category: 'Certificate',
                    },
                    {
                      field: 'IssuerName',
                      label: 'Issuer Name',
                      category: 'Certificate',
                    },
                    {
                      field: 'IssuingOffice',
                      label: 'Issuing Office',
                      category: 'Certificate',
                    },
                    {
                      field: 'AssessmentDate',
                      label: 'Assessment Date',
                      category: 'Certificate',
                    },

                    // Importer Information
                    {
                      field: 'ImporterName',
                      label: 'Importer Name',
                      category: 'Importer',
                    },
                    {
                      field: 'ImporterAddress',
                      label: 'Importer Address',
                      category: 'Importer',
                    },
                    {
                      field: 'ImporterCity',
                      label: 'Importer City',
                      category: 'Importer',
                    },
                    {
                      field: 'ImporterCountry',
                      label: 'Importer Country',
                      category: 'Importer',
                    },

                    // Exporter Information
                    {
                      field: 'ExporterName',
                      label: 'Exporter Name',
                      category: 'Exporter',
                    },
                    {
                      field: 'ExporterAddress',
                      label: 'Exporter Address',
                      category: 'Exporter',
                    },
                    {
                      field: 'ExporterCountry',
                      label: 'Exporter Country',
                      category: 'Exporter',
                    },

                    // Invoice Information
                    {
                      field: 'InvoiceNumber',
                      label: 'Invoice Number',
                      category: 'Invoice',
                    },
                    {
                      field: 'InvoiceDate',
                      label: 'Invoice Date',
                      category: 'Invoice',
                    },
                    {
                      field: 'InvoiceValue',
                      label: 'Invoice Value',
                      category: 'Invoice',
                    },
                    {
                      field: 'InvoiceCurrency',
                      label: 'Invoice Currency',
                      category: 'Invoice',
                    },
                    {
                      field: 'DeclaredValuePerContainer',
                      label: 'Declared Value Per Container',
                      category: 'Invoice',
                    },
                    {
                      field: 'InvoiceValueFeeBasis',
                      label: 'Invoice Value (Fee Basis)',
                      category: 'Invoice',
                    },
                    {
                      field: 'Incoterms',
                      label: 'Incoterms',
                      category: 'Invoice',
                    },

                    // Shipment Details
                    {
                      field: 'ShipmentType',
                      label: 'Shipment Type',
                      category: 'Shipment',
                    },
                    { field: 'Branch', label: 'Branch', category: 'Shipment' },
                    {
                      field: 'CountryOrigin',
                      label: 'Country of Origin',
                      category: 'Shipment',
                    },
                    {
                      field: 'CountryOfShipment',
                      label: 'Country of Shipment',
                      category: 'Shipment',
                    },
                    {
                      field: 'PointOfEntry',
                      label: 'Point of Entry',
                      category: 'Shipment',
                    },

                    // Container & Packaging
                    {
                      field: 'ContainersCount',
                      label: 'Containers Count',
                      category: 'Container',
                    },
                    {
                      field: 'PackingDetails',
                      label: 'Packing Details',
                      category: 'Container',
                    },
                    {
                      field: 'ContainerDetails',
                      label: 'Container Details',
                      category: 'Container',
                    },
                    {
                      field: 'NumberAndTypeOfPackage',
                      label: 'Number and Type of Package',
                      category: 'Container',
                    },
                    {
                      field: 'ContainerSealNumber',
                      label: 'Container Seal Number',
                      category: 'Container',
                    },

                    // Additional Shipment Information
                    {
                      field: 'DocumentNumber',
                      label: 'Document Number',
                      category: 'Additional',
                    },
                    {
                      field: 'DocumentDate',
                      label: 'Document Date',
                      category: 'Additional',
                    },
                    {
                      field: 'BlNo',
                      label: 'BL Number',
                      category: 'Additional',
                    },
                    {
                      field: 'BlDate',
                      label: 'BL Date',
                      category: 'Additional',
                    },
                    {
                      field: 'Signee',
                      label: 'Signee',
                      category: 'Additional',
                    },
                    {
                      field: 'Remarks',
                      label: 'Remarks',
                      category: 'Additional',
                    },
                  ];

                  return (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 500, overflow: 'auto' }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Category</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Field</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Value</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Status</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allFieldsToShow.map(({ field, label, category }) => {
                            const value = previewData.certificate[field];
                            const hasValue =
                              value !== null &&
                              value !== undefined &&
                              value.toString().trim() !== '';
                            const isRequired = requiredFields.includes(field);

                            return (
                              <TableRow key={field}>
                                <TableCell>
                                  <Chip
                                    label={category}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {label}
                                  {isRequired && (
                                    <span style={{ color: '#ef4444' }}> *</span>
                                  )}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {hasValue ? value.toString() : '-'}
                                </TableCell>
                                <TableCell>
                                  {isRequired ? (
                                    hasValue ? (
                                      <Chip
                                        label="✓"
                                        size="small"
                                        color="success"
                                      />
                                    ) : (
                                      <Chip
                                        label="Missing"
                                        size="small"
                                        color="error"
                                      />
                                    )
                                  ) : (
                                    <Chip
                                      label={hasValue ? 'Filled' : 'Optional'}
                                      size="small"
                                      color={hasValue ? 'info' : 'default'}
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                })()}

                {/* Summary Stats - Enhanced with dropdown validation */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: isDarkMode
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'rgba(59, 130, 246, 0.05)',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    📊 Certificate Field Status:
                  </Typography>
                  {(() => {
                    const requiredFields = [
                      // 'ReferenceNumber', // REMOVED: No longer required
                      'IssuerName',
                      'IssuingOffice',
                      'AssessmentDate',
                      'ImporterName',
                      'ImporterCity',
                      'ImporterCountry',
                      'ExporterName',
                      'ExporterAddress',
                      'ExporterCountry',
                      'InvoiceNumber',
                      'InvoiceDate',
                      'InvoiceValue',
                      'InvoiceCurrency',
                      'DeclaredValuePerContainer',
                      'InvoiceValueFeeBasis',
                      'ShipmentType',
                      'Branch',
                      'CountryOrigin',
                      'CountryOfShipment',
                      'PointOfEntry',
                      'ContainersCount',
                      'PackingDetails',
                      'ContainerDetails',
                      'NumberAndTypeOfPackage',
                    ];

                    const filledRequired = requiredFields.filter(field => {
                      const value = previewData.certificate[field];
                      return (
                        value !== null &&
                        value !== undefined &&
                        value.toString().trim() !== ''
                      );
                    }).length;

                    const totalOptional =
                      Object.keys(previewData.certificate).length -
                      requiredFields.length;
                    const filledOptional = Object.keys(
                      previewData.certificate
                    ).filter(field => {
                      if (requiredFields.includes(field) || field === 'Items')
                        return false;
                      const value = previewData.certificate[field];
                      return (
                        value !== null &&
                        value !== undefined &&
                        value.toString().trim() !== ''
                      );
                    }).length;

                    // Check dropdown field usage
                    const dropdownFields = {
                      Incoterms: previewData.certificate.Incoterms,
                      ShipmentType: previewData.certificate.ShipmentType,
                      PointOfEntry: previewData.certificate.PointOfEntry,
                      InvoiceCurrency: previewData.certificate.InvoiceCurrency,
                    };

                    const validDropdowns = Object.entries(
                      dropdownFields
                    ).filter(([field, value]) => {
                      if (!value) return false;
                      switch (field) {
                        case 'Incoterms':
                          return DROPDOWN_OPTIONS.incoterms.includes(value);
                        case 'ShipmentType':
                          return DROPDOWN_OPTIONS.shipmentType.includes(value);
                        case 'PointOfEntry':
                          return DROPDOWN_OPTIONS.pointOfEntry.includes(value);
                        case 'InvoiceCurrency':
                          return DROPDOWN_OPTIONS.currency.includes(value);
                        default:
                          return false;
                      }
                    }).length;

                    return (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Required: ${filledRequired}/${requiredFields.length}`}
                          size="small"
                          color={
                            filledRequired === requiredFields.length
                              ? 'success'
                              : 'error'
                          }
                        />
                        <Chip
                          label={`Optional: ${filledOptional}/${totalOptional}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Dropdown Fields: ${validDropdowns}/4 valid`}
                          size="small"
                          color={
                            validDropdowns === 4
                              ? 'success'
                              : validDropdowns > 0
                                ? 'warning'
                                : 'default'
                          }
                        />
                        <Chip
                          label={`Total Fields: ${Object.keys(previewData.certificate).length - 1}`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </Box>
                    );
                  })()}

                  {/* Show dropdown values used */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: isDarkMode ? '#94a3b8' : '#6b7280',
                    }}
                  >
                    🎯 Dropdown Values: Incoterms=
                    {previewData.certificate.Incoterms || 'None'}, ShipmentType=
                    {previewData.certificate.ShipmentType || 'None'},
                    PointOfEntry=
                    {previewData.certificate.PointOfEntry || 'None'}, Currency=
                    {previewData.certificate.InvoiceCurrency || 'None'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Items Preview - Enhanced with all important fields */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  📦 Items Data Preview ({previewData.items.length} items)
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 400, overflow: 'auto' }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>#</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Type *</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Description *</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Quantity *</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Value *</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Currency</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Origin *</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Standard Ref *</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Packing *</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Slaughter Date</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {item.Type ? (
                              DROPDOWN_OPTIONS.itemType.includes(item.Type) ? (
                                <Chip
                                  label={item.Type}
                                  size="small"
                                  color="primary"
                                />
                              ) : (
                                <Chip
                                  label={`${item.Type} ❌`}
                                  size="small"
                                  color="error"
                                  title="Invalid item type"
                                />
                              )
                            ) : (
                              <Chip
                                label="Missing"
                                size="small"
                                color="error"
                              />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {item.ItemDescription || '-'}
                          </TableCell>
                          <TableCell>{item.Quantity || '-'}</TableCell>
                          <TableCell>{item.GoodsValue || '-'}</TableCell>
                          <TableCell>
                            {item.Currency ? (
                              DROPDOWN_OPTIONS.currency.includes(
                                item.Currency
                              ) ? (
                                item.Currency
                              ) : (
                                <span style={{ color: '#ef4444' }}>
                                  {item.Currency} ❌
                                </span>
                              )
                            ) : (
                              'USD'
                            )}
                          </TableCell>
                          <TableCell>{item.Origin || '-'}</TableCell>
                          <TableCell>{item.StandardRefNumber || '-'}</TableCell>
                          <TableCell>{item.Packing || '-'}</TableCell>
                          <TableCell>
                            {item.SlotDate ? (
                              <Chip
                                label={new Date(
                                  item.SlotDate
                                ).toLocaleDateString()}
                                size="small"
                                color="success"
                              />
                            ) : item.Type === 'Food' ? (
                              <Chip
                                label="Required for Food"
                                size="small"
                                color="warning"
                              />
                            ) : (
                              <Chip
                                label="N/A"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Field Status Summary - Enhanced with dropdown validation */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: isDarkMode
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'rgba(59, 130, 246, 0.05)',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    📊 Items Field Status:
                  </Typography>
                  {(() => {
                    const validTypes = previewData.items.filter(
                      item =>
                        item.Type &&
                        DROPDOWN_OPTIONS.itemType.includes(item.Type)
                    ).length;

                    const validCurrencies = previewData.items.filter(
                      item =>
                        item.Currency &&
                        DROPDOWN_OPTIONS.currency.includes(item.Currency)
                    ).length;

                    const foodItemsWithSlotDate = previewData.items.filter(
                      item => item.Type === 'Food' && item.SlotDate
                    ).length;

                    const totalFoodItems = previewData.items.filter(
                      item => item.Type === 'Food'
                    ).length;

                    return (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label="* = Required Field"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                        <Chip
                          label={`Valid Item Types: ${validTypes}/${previewData.items.length}`}
                          size="small"
                          color={
                            validTypes === previewData.items.length
                              ? 'success'
                              : 'warning'
                          }
                        />
                        <Chip
                          label={`Valid Currencies: ${validCurrencies}/${previewData.items.length}`}
                          size="small"
                          color={
                            validCurrencies === previewData.items.length
                              ? 'success'
                              : 'warning'
                          }
                        />
                        {totalFoodItems > 0 && (
                          <Chip
                            label={`Food Items w/ Slaughter Date: ${foodItemsWithSlotDate}/${totalFoodItems}`}
                            size="small"
                            color={
                              foodItemsWithSlotDate === totalFoodItems
                                ? 'success'
                                : 'warning'
                            }
                          />
                        )}
                      </Box>
                    );
                  })()}

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: isDarkMode ? '#94a3b8' : '#6b7280',
                    }}
                  >
                    🎯 Valid Item Types: {DROPDOWN_OPTIONS.itemType.join(', ')}{' '}
                    • Valid Currencies: {DROPDOWN_OPTIONS.currency.join(', ')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CustomButton variant="outlined" onClick={handleClose}>
          Cancel
        </CustomButton>

        {activeStep === 1 && (
          <CustomButton variant="outlined" onClick={() => setActiveStep(0)}>
            Back
          </CustomButton>
        )}

        {activeStep === 1 && (
          <CustomButton
            variant="gradient"
            startIcon={<Eye size={16} />}
            onClick={() => setActiveStep(2)}
            disabled={validationErrors.length > 0}
          >
            Preview Import
          </CustomButton>
        )}

        {activeStep === 2 && (
          <CustomButton
            variant="gradient"
            startIcon={<Import size={16} />}
            onClick={handleImport}
            disabled={validationErrors.length > 0}
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              },
            }}
          >
            Import Certificate
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportCertificateDialog;
